import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const SORT_MODES = [
  { key: "az", labelKey: "line_sorter_sort_az" },
  { key: "za", labelKey: "line_sorter_sort_za" },
  { key: "natural", labelKey: "line_sorter_sort_natural" },
  { key: "length-asc", labelKey: "line_sorter_sort_length_asc" },
  { key: "length-desc", labelKey: "line_sorter_sort_length_desc" },
  { key: "reverse", labelKey: "line_sorter_sort_reverse" },
  { key: "shuffle", labelKey: "line_sorter_sort_shuffle" },
  { key: "none", labelKey: "line_sorter_sort_none" },
];

const DEFAULT_INPUT = [
  "banana",
  "Apple",
  "cherry",
  "apple",
  "Date",
  "elderberry",
  "banana",
  "",
  "  fig  ",
  "grape",
].join("\n");

const shuffleArray = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const processLines = (raw, opts) => {
  if (!raw) {
    return { output: "", inputLines: 0, duplicatesRemoved: 0, emptyRemoved: 0 };
  }
  const {
    sortMode,
    caseSensitive,
    trim,
    dedupe,
    ignoreEmpty,
    locale,
  } = opts;

  const rawLines = raw.split(/\r?\n/);
  const inputLines = rawLines.length;

  let lines = trim ? rawLines.map((l) => l.trim()) : rawLines.slice();

  let emptyRemoved = 0;
  if (ignoreEmpty) {
    const before = lines.length;
    lines = lines.filter((l) => l.length > 0);
    emptyRemoved = before - lines.length;
  }

  let duplicatesRemoved = 0;
  if (dedupe) {
    const seen = new Set();
    const kept = [];
    for (const line of lines) {
      const key = caseSensitive ? line : line.toLocaleLowerCase();
      if (seen.has(key)) {
        duplicatesRemoved += 1;
        continue;
      }
      seen.add(key);
      kept.push(line);
    }
    lines = kept;
  }

  const naturalCollator = new Intl.Collator(locale, {
    numeric: true,
    sensitivity: caseSensitive ? "variant" : "base",
  });
  const plainCollator = new Intl.Collator(locale, {
    sensitivity: caseSensitive ? "variant" : "base",
  });

  switch (sortMode) {
    case "az":
      lines.sort((a, b) => plainCollator.compare(a, b));
      break;
    case "za":
      lines.sort((a, b) => plainCollator.compare(b, a));
      break;
    case "natural":
      lines.sort((a, b) => naturalCollator.compare(a, b));
      break;
    case "length-asc":
      lines.sort((a, b) => a.length - b.length || plainCollator.compare(a, b));
      break;
    case "length-desc":
      lines.sort((a, b) => b.length - a.length || plainCollator.compare(a, b));
      break;
    case "reverse":
      lines.reverse();
      break;
    case "shuffle":
      lines = shuffleArray(lines);
      break;
    case "none":
    default:
      break;
  }

  return {
    output: lines.join("\n"),
    inputLines,
    outputLines: lines.length,
    duplicatesRemoved,
    emptyRemoved,
  };
};

const LineSorter = () => {
  const { t, i18n } = useTranslation();
  const [input, setInput] = useState(DEFAULT_INPUT);
  const [sortMode, setSortMode] = useState("az");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [trim, setTrim] = useState(true);
  const [dedupe, setDedupe] = useState(true);
  const [ignoreEmpty, setIgnoreEmpty] = useState(true);
  // Bumped to force a fresh random order while staying in shuffle mode.
  const [shuffleSeed, setShuffleSeed] = useState(0);

  const result = useMemo(
    () =>
      processLines(input, {
        sortMode,
        caseSensitive,
        trim,
        dedupe,
        ignoreEmpty,
        locale: i18n.language || "en",
      }),
    // shuffleSeed is intentionally a dependency so "Reshuffle" recomputes.
    [input, sortMode, caseSensitive, trim, dedupe, ignoreEmpty, i18n.language, shuffleSeed],
  );

  const reshuffle = () => setShuffleSeed((n) => n + 1);

  const selectSortMode = (key) => {
    if (key === "shuffle" && sortMode === "shuffle") {
      reshuffle();
      return;
    }
    setSortMode(key);
  };

  const copy = async () => {
    if (!result.output) {
      toast.error(t("line_sorter_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(result.output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const download = () => {
    if (!result.output) {
      toast.error(t("line_sorter_nothing_to_copy"));
      return;
    }
    const blob = new Blob([result.output], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sorted-lines.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clear = () => setInput("");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("line_sorter_title")}
            </h1>
            <p className="text-gray-600">{t("line_sorter_intro")}</p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("line_sorter_input_label")}
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("line_sorter_input_placeholder")}
                rows={10}
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("line_sorter_order_label")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {SORT_MODES.map((s) => (
                    <StandardButton
                      key={s.key}
                      onClick={() => selectSortMode(s.key)}
                      aria-pressed={sortMode === s.key}
                      type={sortMode === s.key ? "primary" : "secondary"}
                      variant={sortMode === s.key ? undefined : "outlined"}
                      size="small"
                    >
                      {t(s.labelKey)}
                    </StandardButton>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={dedupe}
                    onChange={(e) => setDedupe(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("line_sorter_dedupe")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={trim}
                    onChange={(e) => setTrim(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("line_sorter_trim")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={ignoreEmpty}
                    onChange={(e) => setIgnoreEmpty(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("line_sorter_ignore_empty")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={caseSensitive}
                    onChange={(e) => setCaseSensitive(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("line_sorter_case_sensitive")}
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="text-sm font-medium text-gray-700">
                  {t("line_sorter_output_label")}
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {sortMode === "shuffle" && (
                    <StandardButton
                      onClick={reshuffle}
                      type="secondary"
                      variant="outlined"
                      size="small"
                      disabled={!result.output}
                    >
                      {t("line_sorter_reshuffle")}
                    </StandardButton>
                  )}
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!input}
                  >
                    {t("line_sorter_clear")}
                  </StandardButton>
                  <StandardButton
                    onClick={download}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!result.output}
                  >
                    {t("line_sorter_download")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!result.output}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              <pre className="w-full min-h-[8rem] max-h-[24rem] overflow-auto p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap break-all text-gray-800">
                {result.output || (
                  <em className="text-gray-400 not-italic">
                    {t("line_sorter_output_empty")}
                  </em>
                )}
              </pre>
              <div className="text-xs text-gray-500">
                {t("line_sorter_stats", {
                  input: result.inputLines,
                  output: result.outputLines ?? 0,
                  duplicates: result.duplicatesRemoved,
                  empty: result.emptyRemoved,
                })}
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("line_sorter_note_natural")}</p>
              <p>{t("line_sorter_note_locale")}</p>
              <p>{t("line_sorter_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default LineSorter;
